import { useTranslation } from 'react-i18next';

import { Alert, ProgressBar } from '@/shared/ui/atoms';

import type { TutorStudentUsage } from '../../../types/tutorAi';
import { formatCount, formatPercent } from '../../../utils/format';
import { KpiCard } from '../../atoms/KpiCard';

export interface StudentLimitKpiRowProps {
  readonly usage: TutorStudentUsage;
}

/**
 * Indicadores de limite de token de um aluno: diário, semanal e mensal.
 *
 * `limite = 0` **não** significa zero tokens permitidos — significa que o aluno não tem
 * limite individual e vale o padrão global. Exibir `0` faria o operador ler o oposto do que
 * o dado diz, então o card informa a ausência de limite em palavras.
 *
 * `limitEnabled = false` desliga o controle inteiro: os valores configurados existem mas
 * são ignorados pelo backend, e a tela precisa dizer isso antes dos números. Além do alerta,
 * os três cards trocam o "de {{limit}}" por um aviso de "não aplicado" e a barra de
 * progresso mensal não é desenhada — mostrar um percentual de um teto que o backend ignora
 * afirmaria um corte que não existe. O consumo em si continua visível: é dado real.
 *
 * `*LimitReached` significa que o aluno está cortado agora, então vira aviso acima dos
 * cards — não uma variação de cor que passaria batida.
 */
export function StudentLimitKpiRow({ usage }: StudentLimitKpiRowProps) {
  const { t } = useTranslation('tutorAI');

  const anyLimitReached =
    usage.dailyLimitReached || usage.weeklyLimitReached || usage.monthlyLimitReached;

  const windows = [
    {
      key: 'daily',
      label: t('student.limits.daily'),
      used: usage.dailyTokensUsed,
      limit: usage.dailyLimit,
    },
    {
      key: 'weekly',
      label: t('student.limits.weekly'),
      used: usage.weeklyTokensUsed,
      limit: usage.weeklyLimit,
    },
    {
      key: 'monthly',
      label: t('student.limits.monthly'),
      used: usage.monthlyTokensUsed,
      limit: usage.monthlyLimit,
    },
  ] as const;

  return (
    <div className="flex flex-col gap-mini">
      {usage.limitEnabled ? null : (
        <Alert type="information" description={t('student.limits.disabled')} />
      )}

      {anyLimitReached ? <Alert type="warning" description={t('student.limits.reached')} /> : null}

      <div className="grid grid-cols-1 gap-mini sm:grid-cols-3">
        {windows.map(item => (
          <KpiCard
            key={item.key}
            label={item.label}
            value={formatCount(item.used)}
            hint={
              !usage.limitEnabled
                ? t('student.limits.notApplied')
                : item.limit === 0
                  ? t('student.limits.noLimit')
                  : t('student.limits.ofLimit', { limit: formatCount(item.limit) })
            }
          />
        ))}
      </div>

      {usage.limitEnabled && usage.monthlyLimit > 0 ? (
        <div className="flex flex-col gap-quark">
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('student.limits.monthlyProgress', {
              percent: formatPercent(usage.monthlyPercentUsed),
            })}
          </span>
          <ProgressBar progressPercentage={usage.monthlyPercentUsed} />
        </div>
      ) : null}
    </div>
  );
}
